/*
 * Cart drawer.
 *
 * Built to feel instant:
 * - Add to cart opens the drawer on the tap with an "Adding…" row; the server's
 *   render of the drawer then morphs in place (snippets/cart-drawer markup).
 * - Quantity and remove update prices, subtotal, counts and the free-shipping bar
 *   immediately from per-line unit prices, then sync in the background. Rapid taps
 *   are batched into one /cart/update.js call that also returns the rendered drawer.
 * - Server HTML is applied with Idiomorph, so focus, scroll position and the upsell
 *   row survive updates instead of the whole drawer being swapped out.
 */

const cartDrawerMoney = (cents) => {
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency
    }).format(cents / 100);
  } catch (error) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
};

// Morph target's children into source's children; falls back to innerHTML without Idiomorph
const cartDrawerMorph = (target, source) => {
  if (!window.Idiomorph) {
    target.innerHTML = source.innerHTML;
    return;
  }
  Idiomorph.morph(target, source.innerHTML, {
    morphStyle: 'innerHTML',
    // No ignoreActiveValue: once the drawer opens, focus sits on .drawer__inner, and
    // Idiomorph would then leave that whole subtree untouched. Renders only land after
    // the shopper stops changing quantities, so a focused input already holds the value.
    callbacks: {
      beforeNodeMorphed: (oldNode, newNode) => {
        // Keep an upsell row the browser filled from /recommendations while it still
        // belongs to the same last item; the server sends that row empty
        if (
          oldNode.nodeType === 1 &&
          oldNode.classList.contains('cart-upsell-track') &&
          !newNode.hasAttribute('data-server-rendered') &&
          oldNode.childElementCount > 0 &&
          oldNode.dataset.upsellFor === newNode.getAttribute('data-upsell-for')
        ) {
          return false;
        }
        return true;
      }
    }
  });
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Delegated so it keeps working after the drawer markup is morphed
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
      const checkout = event.target.closest('#CartDrawer-Checkout');
      if (checkout) checkout.classList.add('loading');
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (this.staleCheckReady) return;
    this.staleCheckReady = true;
    // Pages fetched ahead of time (speculation-rules prefetch/prerender, or restored
    // from the back/forward cache) carry the cart as it was when they were fetched.
    // Stamp every cart change, and re-render if one happened since then.
    const stampKey = 'mtb-cart-changed-at';
    const changedSince = (time) => {
      try {
        return Number(localStorage.getItem(stampKey) || 0) > time;
      } catch (error) {
        return false;
      }
    };
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        try {
          localStorage.setItem(stampKey, String(Date.now()));
        } catch (error) {}
      });
    }
    const nav = performance.getEntriesByType('navigation')[0];
    const speculative = nav && (nav.activationStart > 0 || nav.deliveryType === 'navigational-prefetch');
    // Prefetched documents stay usable for up to 5 minutes
    if (speculative && changedSince(Date.now() - 5 * 60 * 1000)) this.refresh();
    let hiddenAt = 0;
    window.addEventListener('pagehide', () => {
      hiddenAt = Date.now();
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && changedSince(hiddenAt)) this.refresh();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    if (!this.classList.contains('active')) {
      // One frame so the slide-in transition runs from the closed position
      requestAnimationFrame(() => this.classList.add('animate', 'active'));
      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );
    }

    document.body.classList.add('overflow-hidden');
    startTimer();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Optimistic add. A product that is already a line just gets its quantity bumped in
  // place; anything new appears as a real line at the top of the list, which is where
  // Shopify puts new lines, so the server render lands without anything moving.
  showPending({ title, image, variantTitle, price, priceLabel, quantity = 1, variantId, sellingPlanId } = {}) {
    this.clearPending();
    this.setBusy(true);
    this.wasEmpty = this.classList.contains('is-empty');
    const items = this.querySelector('cart-drawer-items');

    const existing =
      variantId &&
      [...this.querySelectorAll('.cart-item[data-variant-id]')].find(
        (row) =>
          Number(row.dataset.variantId) === Number(variantId) &&
          String(row.dataset.sellingPlan || '') === String(sellingPlanId || '')
      );
    if (existing && items && typeof items.applyOptimistic === 'function') {
      const input = existing.querySelector('.quantity__input');
      items.applyOptimistic(existing, (Number(input && input.value) || 0) + quantity);
      existing.classList.remove('is-bumped');
      void existing.offsetWidth;
      existing.classList.add('is-bumped');
      this.pendingExisting = existing;
      this.open();
      return;
    }

    let tbody = this.querySelector('.cart-items tbody');
    if (!tbody) {
      // Empty cart: give the line a list to sit in until the server render replaces it
      const contents = this.querySelector('#CartDrawer-CartItems');
      if (!contents) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'drawer__cart-items-wrapper';
      const table = document.createElement('table');
      table.className = 'cart-items';
      table.setAttribute('role', 'table');
      tbody = document.createElement('tbody');
      table.append(tbody);
      wrapper.append(table);
      contents.prepend(wrapper);
      this.pendingShell = wrapper;
      if (items) items.classList.remove('is-empty');
    }

    const row = this.buildPendingRow({ title, image, variantTitle, price, priceLabel, quantity });
    tbody.prepend(row);
    this.pendingRow = row;
    this.classList.remove('is-empty');
    if (items && typeof items.refreshTotals === 'function') {
      items.refreshTotals();
    } else {
      this.bumpCounts(quantity);
    }
    this.open();
  }

  // Same cells and classes as a line from snippets/cart-drawer, so it lays out identically
  buildPendingRow({ title, image, variantTitle, price, priceLabel, quantity }) {
    const cell = (className) => {
      const td = document.createElement('td');
      td.className = className;
      td.setAttribute('role', 'cell');
      return td;
    };
    const row = document.createElement('tr');
    row.className = 'cart-item cart-item--pending';
    row.setAttribute('role', 'row');
    row.setAttribute('aria-busy', 'true');
    row.dataset.pendingQuantity = quantity;
    if (typeof price === 'number') row.dataset.pendingTotal = price;

    const media = cell('cart-item__media');
    const frame = document.createElement('div');
    frame.className = 'cart-item__pending-image';
    if (image) {
      const img = document.createElement('img');
      img.className = 'cart-item__image';
      img.src = image;
      img.alt = '';
      img.width = 150;
      img.height = 150;
      img.decoding = 'async';
      img.addEventListener('load', () => frame.classList.add('is-loaded'), { once: true });
      frame.append(img);
    }
    media.append(frame);

    const details = cell('cart-item__details');
    const name = document.createElement('span');
    name.className = 'cart-item__name ctm_general_content heading_text text_blue_charcoal break';
    name.textContent = title || '';
    details.append(name);
    const amountText = typeof price === 'number' ? cartDrawerMoney(price / (quantity || 1)) : priceLabel;
    if (amountText) {
      const unit = document.createElement('div');
      unit.className = 'product-option';
      unit.textContent = amountText;
      details.append(unit);
    }
    if (variantTitle) {
      const option = document.createElement('div');
      option.className = 'product-option';
      option.textContent = variantTitle;
      details.append(option);
    }
    const status = document.createElement('div');
    status.className = 'cart-item__pending-status';
    status.setAttribute('role', 'status');
    status.textContent = 'Adding…';
    details.append(status);

    const totals = cell('cart-item__totals right');
    if (typeof price === 'number' || priceLabel) {
      const total = document.createElement('span');
      total.className = 'price price--end';
      total.textContent = typeof price === 'number' ? cartDrawerMoney(price) : priceLabel;
      totals.append(total);
    }

    const quantityCell = cell('cart-item__quantity');
    const skeleton = document.createElement('span');
    skeleton.className = 'cart-item__pending-quantity';
    quantityCell.append(skeleton);

    row.append(media, details, totals, quantityCell, cell('ctm_cart_removebtn'));
    return row;
  }

  setBusy(on) {
    this.classList.toggle('is-busy', on);
  }

  bumpCounts(delta) {
    const bump = (element, wrap) => {
      if (!element) return;
      const current = parseInt(element.textContent.replace(/[^0-9]/g, ''), 10) || 0;
      element.textContent = wrap ? `(${current + delta})` : `${current + delta}`;
    };
    bump(this.querySelector('.drawer__heading .cart-count-bubble [aria-hidden]'), true);
    bump(document.querySelector('#cart-icon-bubble .cart-count-bubble [aria-hidden]'), false);
  }

  showPendingError(message) {
    const text = message || (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
    this.setBusy(false);
    clearTimeout(this.pendingErrorTimer);
    if (this.pendingRow) {
      this.pendingRow.classList.add('is-error');
      const status = this.pendingRow.querySelector('.cart-item__pending-status');
      if (status) {
        status.setAttribute('role', 'alert');
        status.textContent = text;
      }
      this.pendingErrorTimer = setTimeout(() => {
        this.clearPending();
        if (this.wasEmpty && !this.querySelector('.cart-item')) this.classList.add('is-empty');
        // Put counts and totals back to the server's numbers
        this.refresh();
      }, 4000);
      return;
    }
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = text;
    this.clearPending();
    this.refresh();
  }

  clearPending() {
    clearTimeout(this.pendingErrorTimer);
    if (this.pendingRow) this.pendingRow.remove();
    if (this.pendingShell) this.pendingShell.remove();
    this.pendingRow = null;
    this.pendingShell = null;
    this.pendingExisting = null;
    this.setBusy(false);
  }

  renderContents(parsedState, { open = true } = {}) {
    this.clearPending();
    this.productId = parsedState.id;
    const sections = parsedState.sections || {};
    this.getSectionsToRender().forEach((section) => {
      const html = sections[section.id];
      if (!html) return;
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const source = this.getSectionDOM(html, section.selector);
      if (target && source) cartDrawerMorph(target, source);
    });

    this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
    if (open) this.open();
    document.dispatchEvent(new CustomEvent('cart-drawer:rendered'));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  // Re-render from the server, e.g. after another script changed the cart
  async refresh() {
    const ids = this.getSectionsToRender().map((section) => section.id);
    try {
      const sections = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((response) =>
        response.json()
      );
      this.renderContents({ sections }, { open: false });
    } catch (error) {
      console.error(error);
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.pendingUpdates = new Map();
    this.flushTimer = null;
    this.inFlight = false;
    // React on the tap itself; CartItems' debounced handler is replaced by onChange below
    this.addEventListener('change', (event) => this.onImmediateChange(event));
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // This drawer already rendered these: its own updates, and add to cart (product-form
      // calls renderContents with the add response)
      if (event.source === 'cart-items' || event.source === 'product-form') return;
      const drawer = this.closest('cart-drawer');
      if (drawer) drawer.refresh();
    });
  }

  onChange() {}

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  onImmediateChange(event) {
    const input = event.target;
    if (!input.classList || !input.classList.contains('quantity__input')) return;
    const row = input.closest('.cart-item');
    if (!row) return;
    this.stage(row, Math.max(0, parseInt(input.value, 10) || 0));
  }

  // Called by cart-remove-button with the 1-based line index
  updateQuantity(line, quantity) {
    const row = this.querySelector(`.cart-item[data-index="${line}"]`);
    if (row) this.stage(row, Math.max(0, Number(quantity) || 0));
  }

  stage(row, quantity) {
    const key = row.dataset.lineKey;
    if (!key) return;
    this.applyOptimistic(row, quantity);
    this.pendingUpdates.set(key, quantity);
    clearTimeout(this.flushTimer);
    // Removals go out at once; quantity taps are batched briefly so a burst is one request
    this.flushTimer = setTimeout(() => this.flush(), quantity === 0 ? 0 : 220);
  }

  applyOptimistic(row, quantity) {
    const input = row.querySelector('.quantity__input');
    if (input && Number(input.value) !== quantity) input.value = quantity;
    row.classList.toggle('is-removing', quantity === 0);

    const unitFinal = Number(row.dataset.unitFinal) || 0;
    const unitOriginal = Number(row.dataset.unitOriginal) || unitFinal;
    const finalPrice = row.querySelector('.cart-item__totals span.price');
    const originalPrice = row.querySelector('.cart-item__totals s.cart-item__old-price');
    if (finalPrice) finalPrice.textContent = cartDrawerMoney(unitFinal * quantity);
    if (originalPrice) originalPrice.textContent = cartDrawerMoney(unitOriginal * quantity);

    this.refreshTotals();
  }

  // Totals from line prices. Cart-level discounts are applied when the server render lands.
  refreshTotals() {
    const drawer = this.closest('cart-drawer') || document;
    let total = 0;
    let count = 0;
    this.querySelectorAll('.cart-item').forEach((row) => {
      if (row.classList.contains('is-removing')) return;
      if (row.classList.contains('cart-item--pending')) {
        total += Number(row.dataset.pendingTotal) || 0;
        count += Number(row.dataset.pendingQuantity) || 0;
        return;
      }
      const quantity = Number(row.querySelector('.quantity__input')?.value) || 0;
      total += (Number(row.dataset.unitFinal) || 0) * quantity;
      count += quantity;
    });

    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = cartDrawerMoney(total);

    const headingCount = drawer.querySelector('.drawer__heading .cart-count-bubble [aria-hidden]');
    if (headingCount) headingCount.textContent = `(${count})`;
    const headerCount = document.querySelector('#cart-icon-bubble .cart-count-bubble [aria-hidden]');
    if (headerCount) headerCount.textContent = count;

    drawer.querySelectorAll('[data-free-shipping-goal]').forEach((bar) => {
      const goal = Number(bar.dataset.freeShippingGoal) || 0;
      if (!goal) return;
      const progress = bar.querySelector('progress');
      if (progress) {
        progress.max = goal;
        progress.value = Math.min(total, goal);
      }
      const message = bar.querySelector('.progressbar_content');
      if (message) {
        message.textContent =
          total >= goal
            ? "You've unlocked free shipping!"
            : `You are just ${cartDrawerMoney(goal - total)} away from free shipping`;
      }
      const shipping = drawer.querySelector('[data-cart-shipping] .totals__total-value');
      if (shipping) shipping.textContent = total >= goal ? 'Free Shipping' : 'Calculated at checkout';
    });
  }

  async flush() {
    clearTimeout(this.flushTimer);
    if (this.inFlight || this.pendingUpdates.size === 0) return;

    const updates = Object.fromEntries(this.pendingUpdates);
    this.pendingUpdates.clear();
    const drawer = this.closest('cart-drawer');
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
    this.inFlight = true;
    this.classList.add('is-syncing');
    if (drawer) drawer.setBusy(true);

    try {
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          updates,
          sections: drawer ? drawer.getSectionsToRender().map((section) => section.id) : [],
          sections_url: window.location.pathname
        })
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || window.cartStrings.error);
      }
      // Only paint the server's version once the shopper has stopped changing things
      if (this.pendingUpdates.size === 0 && drawer) drawer.renderContents(state, { open: false });
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    } catch (error) {
      if (errors) errors.textContent = error.message || window.cartStrings.error;
      if (drawer) await drawer.refresh();
    } finally {
      this.inFlight = false;
      this.classList.remove('is-syncing');
      if (drawer && !this.pendingUpdates.size) drawer.setBusy(false);
      if (this.pendingUpdates.size) this.flush();
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

var countDown;
var countDownEndsAt;

// Cart reservation countdown: one interval for the page, counted from the first open.
// It only touches the DOM while the drawer is open, and at zero it hides itself
// (the cart is not actually closed, so it must not say so).
function startTimer() {
  if (countDown) return;
  if (!countDownEndsAt) countDownEndsAt = Date.now() + 10 * 60 * 1000;

  var tick = function () {
    var remaining = Math.max(0, Math.round((countDownEndsAt - Date.now()) / 1000));
    var drawer = document.querySelector('cart-drawer');
    var timer = document.querySelector('.count-down-timer');
    var message = timer && timer.querySelector('.message');

    if (remaining === 0) {
      clearInterval(countDown);
      // A class on <html> also hides timers in drawer markup rendered after this point
      document.documentElement.classList.add('cart-timer-expired');
      return;
    }
    if (!message || !drawer || !drawer.classList.contains('active')) return;

    var min = Math.floor(remaining / 60);
    var sec = remaining % 60;
    message.innerHTML = `Your cart is reserved for <span class="c-min">${min}</span>:<span class="c-sec">${sec > 9 ? sec : '0' + sec}</span>`;
  };

  tick();
  countDown = setInterval(tick, 1000);
}
